import React, {useEffect, useState} from 'react';
import {Button, Divider, Drawer, IconButton, Stack, Typography} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {finalize} from 'rxjs';

import {ManageSuppliersService, SuppliersService, suppliersServicesService} from '@store/suppliers/services';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import DetailsForm from '@screens/admin/suppliers/details/catalog/manageService/components/Details.form';
import AvailabilitiesForm from '@screens/admin/suppliers/details/catalog/manageService/components/Availabilities.form';
import PricesForm from '@screens/admin/suppliers/details/catalog/manageService/components/Prices.form';

import {Close} from '@icons/index';

interface ManageServiceDrawerProps {
  fromSupplier?: boolean;
  handleClose?: () => void;
  handleCreated?: (service?: SuppliersService) => void;
  isCreation?: boolean;
}

const ManageServiceDrawer = (props: ManageServiceDrawerProps) => {
  const {fromSupplier, handleClose: handleCloseProps, handleCreated, isCreation} = props;

  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {serviceId} = useParams();

  const [loading, setLoading] = useState(false);

  const [service, setService] = useState<ManageSuppliersService>({});
  const [serviceName, setServiceName] = useState('');

  useEffect(() => {
    if (!isCreation && serviceId) {
      suppliersServicesService.getSupplierServiceById(serviceId).subscribe({
        next: (s) => {
          setService(s);
          setServiceName(s.supplierName || '-');
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    }
  }, [enqueueSnackbar, isCreation, serviceId]);

  if (!isCreation && !service?.id) return null;

  const handleClose = () => handleCloseProps ? handleCloseProps() : navigate('..', {replace: true});

  const updateService = (data: ManageSuppliersService) => setService((state) => ({...state, ...data}));

  const handleSave = () => {
    if (isCreation) {
      if (!service.supplierUnit) return;
      setLoading(true);
      suppliersServicesService.createSuppliersService(service.supplierUnit.id, service).pipe(finalize(() => setLoading(false))).subscribe({
        next: (ss) => {
          enqueueSnackbar(t('suppliers.details.catalog.manage.success.create'), {variant: 'success'});
          handleClose();
          handleCreated?.(ss);
        },
        error: (err) => enqueueSnackbar(err.text, err.options),
      });
    } else if (service.id) {
      setLoading(true);
      suppliersServicesService.updateSupplierService(service.id, service)
        .pipe(
          finalize(() => setLoading(false))
        ).subscribe({
        next: () => {
          enqueueSnackbar(t('suppliers.details.catalog.manage.success.update'), {variant: 'success'});
        }
      });
    }
  };

  const errors = {
    supplier: !fromSupplier ? getFieldError(service.supplier, FieldValidationType.REQUIRED_SELECT_ITEM) : undefined,
    supplierName: getFieldError(service.supplierName, FieldValidationType.REQUIRED_STRING),
    supplierUnit: getFieldError(service.supplierUnit, FieldValidationType.REQUIRED_SELECT_ITEM),
    serviceKind: getFieldError(service.serviceKind, FieldValidationType.REQUIRED_SELECT_ITEM),
  };

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack overflow="hidden">
        <Stack direction="row" justifyContent="flex-end">
          <IconButton onClick={handleClose}>
            <Close/>
          </IconButton>
        </Stack>
        <Stack pr="10px">
          <Stack direction="row" alignItems="center" spacing={1.5} pb="8px">
            <Typography fontSize={24} fontWeight="700">
              {isCreation ? t('suppliers.details.catalog.manage.title.add') : serviceName}
            </Typography>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Stack className="scrollable" overflow="auto" pb="24px" pr="10px">
          <DetailsForm
            fromSupplier={fromSupplier}
            isCreation={isCreation}
            service={service}
            updateService={updateService}
            errors={errors}/>
          <AvailabilitiesForm
            isCreation={isCreation}
            service={service}
            updateService={updateService}/>
          <PricesForm
            isCreation={isCreation}
            service={service}
            updateService={updateService}/>
        </Stack>
        <Stack alignSelf="flex-end" spacing={3} pb="25px" pt="10px" pr="10px">
          <Button
            variant="contained"
            color="primary"
            disabled={loading || checkIfErrors(errors) || !service.prices?.length}
            onClick={handleSave}>
            {t('global.save')}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
};

export default ManageServiceDrawer;
